import { useEffect, useState } from 'react';
import validate from 'validate.js';

import { constraints, getFirstErrorMessage, removeErrorKey } from '@zg-rentals/zillow-rental-manager/utils';

export type UseFormReturn<T extends Record<string, unknown>> = {
  addErrorKey: (name: string, message: string) => void;
  clearErrorObj: () => void;
  clearFieldError: (name: string) => void;
  clearForm: () => void;
  errorObj: Partial<Record<keyof T, string | null>>;
  handleChange: (e: { target: { name: string; files?: FileList | null; type?: string; value?: unknown } }) => void;
  hasErrors: boolean;
  hasFieldError: (name: string, value: unknown) => boolean;
  inputs: T;
  resetForm: () => void;
  updateForm: (newForm?: T) => void;
};

function useForm<T extends Record<string, unknown>>(
  initial: T = {} as T,
  constraintsObj = constraints as Record<string, unknown>,
): UseFormReturn<T> {
  const [errorObj, setErrorObj] = useState<Partial<Record<keyof T, string | null>>>({});
  const [hasErrors, setHasErrors] = useState(false);
  const [inputs, setInputs] = useState<T>(initial);
  const initialValues = Object.values(initial).join('');

  useEffect(() => {
    setInputs(initial);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [initialValues]);

  useEffect(() => {
    const response = Object.values(errorObj).some((value) => value !== null);

    setHasErrors(response);
  }, [errorObj]);

  const clearFieldError = (name: string) => {
    // clear error for the field
    setErrorObj({
      ...errorObj,
      [name]: null,
    });
  };

  const addErrorKey = (name: string, message: string) => {
    setErrorObj({
      ...errorObj,
      [name]: message,
    });
  };

  const clearErrorObj = () => {
    setErrorObj({});
  };

  const hasFieldError = (name: string, value: unknown) => {
    const constraintsForField = {
      [name]: constraintsObj[name as keyof typeof constraintsObj],
    };

    const fieldConstraint = (constraintsForField[name] || {}) as { presence?: { allowEmpty?: boolean } };
    const { presence: { allowEmpty = true } = {} } = fieldConstraint;

    /**
     * not able to allow null and undefined as valid values when validation key numericality has options
     * bypass entire validation check when allowEmpty is true
     */
    // oxlint-disable-next-line no-constant-binary-expression -- pre-existing dead clause, left as-is; behavior fix tracked separately
    const bypassValidation = (value === '' || value === null || !value === undefined) && allowEmpty;

    const response = bypassValidation === false && validate({ [name]: value }, constraintsForField);

    if (response !== undefined && response.hasOwnProperty(name) && response[name].length > 0) {
      const message = removeErrorKey(name, getFirstErrorMessage(response[name]));

      setErrorObj({
        ...errorObj,
        [name]: message,
      });

      return true;
    }

    return false;
  };

  const handleChange = ({
    target: { name, files, type, value },
  }: {
    target: { name: string; files?: FileList | null; type?: string; value?: unknown };
  }) => {
    clearFieldError(name);

    if (type === 'file' && files) {
      [value] = files;
    }

    setInputs({
      ...inputs,
      [name as string]: value,
    });
  };

  function resetForm() {
    setInputs(initial);
  }

  function clearForm() {
    const blankState = Object.fromEntries(Object.entries(inputs).map(([key]) => [key, ''])) as T;
    setInputs(blankState);
  }

  function updateForm(newForm = initial) {
    setInputs({ ...newForm });
  }

  return {
    addErrorKey,
    clearErrorObj,
    clearFieldError,
    clearForm,
    errorObj,
    handleChange,
    hasErrors,
    hasFieldError,
    inputs,
    resetForm,
    updateForm,
  };
}

export default useForm;
